import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Nullable } from 'chaika-common-types';

import { removeItemFromArray } from '@utils/removeItemFromArray';
import { priceService } from '@price/service/price/priceService';

import { OptionType } from '@components/Select';
import { Currency } from 'chaika-enums';
import { legalEntityQueryOptions } from '@organization/queries/legalEntityQueries';
import { store } from '@store';

interface Props {
    includedServices: string[];
    legalEntityIds?: string[];
    onChange(includedServices: Nullable<string[]>): void;
}

interface Row {
    serviceId: string;
    price: number;
}

interface Data {
    total: number;
    disabledButton: boolean;
    rows: Row[];
    currency: Currency;
    onAddClick(): void;
    onDelete(index: number): void;
    onItemChange(params: Nullable<OptionType>, index: number): void;
}

function createEmptyRow(): Row {
    return { serviceId: '', price: 0 };
}

export function useEditor({ includedServices, onChange, legalEntityIds }: Props): Data {
    const [rows, setRows] = useState<Row[]>([createEmptyRow()]);

    const { data: firstLegalEntity } = useQuery(legalEntityQueryOptions.byId(legalEntityIds?.[0]));

    const currency = firstLegalEntity?.currency ?? Currency.AED;

    const total = rows.reduce((acc, { price }) => acc + price, 0);

    useEffect(() => {
        if (includedServices.length === 0) {
            return;
        }

        let cancelled = false;

        (async () => {
            await priceService.loadPriceByServiceIds(includedServices);

            if (cancelled) return;

            const { priceStore } = store;

            const loadedRows = includedServices.map(serviceId => ({
                serviceId,
                price: priceStore.getBasePrice(serviceId, legalEntityIds)?.value?.kopecks ?? 0,
            }));

            setRows(loadedRows);
        })();

        return () => {
            cancelled = true;
        };
    }, [includedServices, legalEntityIds]);

    const disabledButton = useMemo(() => rows.some(({ serviceId }) => !serviceId), [rows]);

    function onAddClick(): void {
        setRows(prevState => [...prevState, createEmptyRow()]);
    }

    async function onItemChange(params: Nullable<OptionType>, index: number): Promise<void> {
        function replaceRow(row: Row): void {
            setRows(prevState => prevState.map((prevRow, i) => (i === index ? row : prevRow)));
        }

        if (!params?.value) {
            replaceRow(createEmptyRow());
            onChange(rows.map((row, i) => (i === index ? '' : row.serviceId)));
            return;
        }

        const serviceId = String(params.value);

        replaceRow({ serviceId, price: 0 });

        await priceService.loadPriceByServiceIds([serviceId]);

        const price = store.priceStore.getBasePrice(serviceId, legalEntityIds)?.value?.kopecks ?? 0;

        replaceRow({ serviceId, price });

        onChange(rows.map((row, i) => (i === index ? serviceId : row.serviceId)));
    }

    function onDelete(index: number): void {
        const newRows = rows.length === 1 ? [createEmptyRow()] : removeItemFromArray(rows, index);

        setRows(newRows);

        onChange(newRows.map(({ serviceId }) => serviceId));
    }

    return {
        disabledButton,
        rows,
        currency,
        total,
        onDelete,
        onItemChange,
        onAddClick,
    };
}
